import { jsPDF } from "jspdf";
import pdfjsLib from "pdfjs-dist";

/**
 * Extracts a specific range of pages from a PDF blob URL and returns a new Blob.
 * @param {string} pdfBlobUrl - The PDF file URL.
 * @param {number} startPage - The starting page (1-based).
 * @param {number} endPage - The ending page (1-based).
 * @returns {Promise<Blob>} - The extracted PDF as a Blob.
 */
export const extractPageRange = async (pdfBlobUrl, startPage, endPage) => {
  const response = await fetch(pdfBlobUrl);
  const pdfData = await response.arrayBuffer();

  const loadingTask = pdfjsLib.getDocument({ data: pdfData });
  const pdf = await loadingTask.promise;
  const newPdf = new jsPDF();

  const totalPages = pdf.numPages;

  // Clamp start and end to valid page numbers
  const start = Math.max(1, startPage);
  const end = Math.min(endPage, totalPages);

  if (start > end) {
    throw new Error(`Invalid page range: PDF has ${totalPages} pages`);
  }

  for (let pageNumber = start; pageNumber <= end; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 2 });

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d", { willReadFrequently: true });
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Fill background white to avoid transparency issues
    context.fillStyle = "white";
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    const imgData = canvas.toDataURL("image/jpeg", 1.0);

    if (pageNumber > start) {
      newPdf.addPage();
    }

    // Scale to fit PDF page
    const pdfWidth = newPdf.internal.pageSize.getWidth();
    const pdfHeight = newPdf.internal.pageSize.getHeight();
    newPdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
  }

  return newPdf.output("blob");
};
